import { FormComponentFactory } from "../FormComponentFactory";
import { Component, ComponentConfig } from "../Component";
import { Fragment, ReactNode, useCallback, useEffect, useMemo } from "react";
import {
  useComponentConfigAtom,
  useComponentCustomOrEditConfigAtom,
  wireStoreAtom,
} from "../state/ComponentAtoms";
import { useAtom } from "jotai";
import { DropdownButton } from "datasheets/views/spreadsheet/DropDownButton";
import { TextField } from "@mui/material";
import { SingleSelection } from "datasheets/components/selectors/SingleSelection";

type ActionParameters = { [name: string]: any };
interface ActionComponent extends ComponentConfig {
  action: string;
  params: ActionParameters;
}
const ComponentName = "Action";
export function registerActionComponent() {
  FormComponentFactory.addComponent(ComponentName, {
    config: () => {
      return {
        action: "doNothing",
        params: [],
        connections: {
          inputs: [],
          outputs: [],
          inputConnectors: {},
          outputConnectors: {},
        },
      };
    },
    component: (config: Component) => {
      return <ActionDisplay config={config} />;
    },
    editor: (config: Component) => {
      return <ActionEditor componentId={config.id} />;
    },
    fixedOutputs: () => {
      return [];
    },
    fixedInputs: () => {
      return ["trigger"];
    },
  });
  // Add old names for legacy config
  FormComponentFactory.addAlias("Action", ComponentName);
}

function ActionDisplay({ config }: { config: Component }) {
  const [vanillaComponentConfig] = useComponentCustomOrEditConfigAtom(
    config.id
  );
  const componentConfig = vanillaComponentConfig as ActionComponent;
  const [wireStore, setWireStore] = useAtom(wireStoreAtom);
  const inputs = componentConfig.connections?.inputConnectors ?? [];
  const triggerKey = "trigger";
  const triggerKeyId = inputs[triggerKey];
  const runAction = useCallback(() => {
    if (!!componentConfig.action) {
      alertFactory[componentConfig.action].run(componentConfig.params);
    }
  }, [componentConfig.action, componentConfig.params]);
  useEffect(() => {
    if (triggerKeyId && wireStore[triggerKeyId]) {
      runAction();
      setWireStore((store) => {
        return { ...store, ...{ [triggerKeyId]: false } };
      });
    }
  }, [triggerKeyId, wireStore, setWireStore]);
  /// Set up handlers for output wiring
  return <div hidden={true} />;
}

function ActionEditor({ componentId }: { componentId: string }) {
  const [config, setConfig] = useComponentConfigAtom(componentId);
  const componentConfig = config.editConfig as ActionComponent;
  const action = useMemo(
    () => componentConfig.action,
    [componentConfig.action]
  );
  const setAction = //useCallback(
    (action: string) => {
      setConfig((config: Component) => {
        return {
          ...config,
          ...{
            editConfig: { ...config.editConfig, ...{ action: action } },
          },
        };
      });
    }; //,
  //    [setConfig]
  //  );
  const setParams = useCallback(
    (params: ActionParameters) => {
      setConfig((config: Component) => {
        return {
          ...config,
          ...{
            editConfig: { ...config.editConfig, ...{ params: params } },
          },
        };
      });
    },
    [setConfig]
  );
  return (
    <Fragment key={"Editor-" + config.id}>
      <div style={{ fontSize: ".5em" }}>{config.id}</div>
      <div>
        <ActionSelector
          onSelect={setAction}
          selection={action}
          params={componentConfig.params}
          setParams={setParams}
        />
      </div>
    </Fragment>
  );
}

function ActionSelector({
  onSelect,
  selection,
  params,
  setParams,
}: {
  onSelect: (value: string) => void;
  selection?: string;
  params: ActionParameters;
  setParams: (params: ActionParameters) => void;
}) {
  const options = Object.values(alertFactory).reduce(
    (prev: { [key: string]: string }, action: Action) => {
      prev[action.displayText] = action.key;
      return prev;
    },
    {}
  );
  const selectedAction = !!selection ? alertFactory[selection] : undefined;
  const editorComponent = !!selectedAction?.editor ? (
    selectedAction?.editor(params, setParams)
  ) : (
    <></>
  );
  return (
    <>
      <SingleSelection
        title="Select Action"
        onChange={onSelect}
        options={options}
        selection={selection ?? ""}
      />
      {editorComponent}
    </>
  );
}

interface Action {
  run: (params: ActionParameters) => void;
  key: string;
  displayText: string;
  editor?: (
    params: ActionParameters,
    setParams: (params: ActionParameters) => void
  ) => ReactNode;
}

const doNothingAction: Action = {
  run: (params: ActionParameters) => {},
  key: "doNothing",
  displayText: "Do Nothing",
};

const showAlertAction: Action = {
  run: (params: ActionParameters) => {
    alert(params["message"] ?? "Button clicked");
  },
  key: "showAlert",
  displayText: "Show Alert",
  editor: (
    params: ActionParameters,
    setParams: (params: ActionParameters) => void
  ) => {
    const message = params["message"] ?? "";
    return (
      <TextField
        label="Message"
        value={message}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const newMessage = event.target.value;
          setParams({ ...params, ...{ message: newMessage } });
        }}
      />
    );
  },
};

const alertFactory: { [key: string]: Action } = {};
function registerAction(action: Action) {
  alertFactory[action.key] = action;
}
registerAction(doNothingAction);
registerAction(showAlertAction);
